/*
 * The "Linked from" card: the other articles whose live or draft text carries a
 * [[kb:…]] token pointing at this one.
 *
 * Presentational - the page owns the request. Two cards read its answer, this
 * one and the Properties card's warning about what a rename will rewrite, and
 * one request per open is the point of having moved it out of the article
 * detail: finding these references means scanning the current and latest
 * revision of every other article, which grows with the knowledge base, and
 * carried on the detail it sat between the author and the text on every open.
 *
 * Three states, and each says something different:
 *
 *   loading   nothing at all. The card used to appear with the article, so an
 *             empty card saying "checking…" would be new furniture for a state
 *             that lasts about a second; the History card below simply moves
 *             down when there turns out to be something to show.
 *   failed    a banner with a retry. A silent failure here would read as "no
 *             other article links here", which is the one thing this card must
 *             never say by accident - it is what an author checks before a
 *             delete or a rename.
 *   loaded    the card when there are references, nothing when there are none,
 *             exactly as before.
 *
 * RELEASE NOTES LINK HERE TOO (S42, 2026-10-12). A release note's text carries
 * the same tokens, and deleting or moving this article rewrites them exactly as
 * it rewrites other articles', so an author checking before a delete has to see
 * them. They arrive on the same response as their own list and are shown as
 * their own sub-list, linked to the release-note editor and named by title and
 * version - two release notes may share a title, never a product and version.
 * The card renders when EITHER list has something; the article rows are as they
 * were, and the count in the header is the two lists together.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48).
 */

import { Link } from 'react-router-dom';
import type { ArticleReference, ReleaseNoteReference } from '@accotool-kb/shared';
import { adminReleaseNoteHref } from '@accotool-kb/shared';
import { ErrorBanner } from '../../components/ui.js';
import { useMessages } from '../../i18n/language.js';
import { richText } from '../../i18n/richText.js';

export function LinkedFromPanel({
  references,
  releaseNotes,
  error,
  onRetry,
}: {
  /** Null while the request is in flight, or when it has not succeeded. */
  references: ArticleReference[] | null;
  /**
   * The release notes linking here, from the same response. Null or absent while
   * it is in flight - the same "not known yet" as `references`.
   */
  releaseNotes?: ReleaseNoteReference[] | null;
  error: Error | null;
  onRetry: () => void;
}): JSX.Element | null {
  const words = useMessages().admin.articles.linkedFrom;

  if (error) {
    return (
      <div className="card">
        <div className="card-header">
          <h3>{words.heading}</h3>
        </div>
        <ErrorBanner error={error} onRetry={onRetry} />
      </div>
    );
  }

  const articles = references ?? [];
  const notes = releaseNotes ?? [];
  if (articles.length === 0 && notes.length === 0) return null;

  // Sub-headings only when there are two kinds to tell apart; a card listing
  // articles alone reads exactly as it always has.
  const both = articles.length > 0 && notes.length > 0;
  const who = both ? 'both' : notes.length > 0 ? 'releaseNotes' : 'articles';

  return (
    <div className="card">
      <div className="card-header">
        <h3>{words.heading}</h3>
        <span className="faint">{articles.length + notes.length}</span>
      </div>
      <p className="faint">{richText(words.intro(who), { token: <code>[[kb:…]]</code> })}</p>

      {articles.length > 0 && (
        <>
          {both && <h4 className="linked-from-heading">{words.articles}</h4>}
          <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.9rem' }}>
            {articles.map((reference) => (
              <li key={`${reference.articleId}-${reference.revisionNumber}`}>
                <Link to={`/admin/articles/${reference.articleId}`}>{reference.title}</Link>{' '}
                <span className="faint">
                  {words.revision(reference.revisionNumber, reference.isCurrent)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {notes.length > 0 && (
        <>
          <h4 className="linked-from-heading">{words.releaseNotes}</h4>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.9rem' }}>
            {notes.map((reference) => (
              <li key={`rn-${reference.releaseNoteId}-${reference.revisionNumber}`}>
                <Link to={adminReleaseNoteHref(reference.releaseNoteId)}>
                  {reference.title} ({reference.version})
                </Link>{' '}
                <span className="faint">
                  {words.revision(reference.revisionNumber, reference.isCurrent)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
